import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSeoMeta } from '@unhead/react';
import { CardList } from '@/components/CardList';
import { useCardList } from '@/hooks/useCardList';
import { ListControls } from '@/components/ListControls';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useState, useCallback, useEffect } from 'react';
import { nip19 } from 'nostr-tools';
import { useNavigate } from 'react-router-dom';
import { ShareListDialog } from '@/components/ShareListDialog';
import { MobileDeckBuilder } from '@/components/MobileDeckBuilder';

import { Deck, prepareDeckEvent } from '@/lib/nostr/deck';
import { ScryfallCard } from '@/types/scryfall';
import { DeckBuilderModal } from '@/components/DeckBuilderModal';
import { CardSearch } from '@/components/CardSearch';
import { PublishConfirmationDialog } from '@/components/PublishConfirmationDialog';
import { useAppContext } from '@/hooks/useAppContext';
import { Button } from '@/components/ui/button';
import { Bot } from 'lucide-react';
import { useEDHRec } from '@/hooks/useEDHRec';
import { EDHRecModal } from '@/components/EDHRecModal';
import { QuickAddBar } from '@/components/QuickAddBar';

const Build = () => {
  const { user } = useCurrentUser();
  const { cards, addCard, removeCard, setCardCount, updateCard, clearList } = useCardList('deck');
  const { cards: sideboardCards, addCard: addSideboardCard, removeCard: removeSideboardCard, setCardCount: setSideboardCardCount, updateCard: updateSideboardCard, clearList: clearSideboardList } = useCardList('sideboard');
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { config } = useAppContext();

  const [title, setTitle] = useState('');
  const [format, setFormat] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'unlisted'>('public');
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [commanderCardIds, setCommanderCardIds] = useState<string[]>([]);
  const [companionCardId, setCompanionCardId] = useState<string | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState<Deck | null>(null);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);

  const [isDeckBuilderOpen, setIsDeckBuilderOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEdhrecModalOpen, setIsEdhrecModalOpen] = useState(false);
  const { mutate: getRecommendations, data: edhrecData, isPending: isEdhrecPending, isError: isEdhrecError } = useEDHRec();

  // Ensure commanderCardIds stay synchronized with actual cards
  useEffect(() => {
    setCommanderCardIds(prev => prev.filter(id => cards.some(card => card.id === id)));
  }, [cards]);

  const handleFetchRecommendations = useCallback(() => {
    const commanderNames = cards.filter(c => commanderCardIds.includes(c.id)).map(c => c.name);
    if (commanderNames.length === 0) return;

    const cardNames = cards.map((c) => `1 ${c.name}`);
    const commanders = commanderNames.map((c) => `1 ${c} *CMDR*`);

    getRecommendations({
      cards: [...cardNames, ...commanders],
      commanders: commanderNames,
    });
    setIsEdhrecModalOpen(true);
  }, [cards, commanderCardIds, getRecommendations]);

  const handleDeckBuilderFinish = (query: string) => {
    setSearchQuery(query);
  };

  const handleOpenPublishDialog = () => {
    setIsPublishConfirmOpen(true);
  };

  const handleSave = () => {
    const commanderNames = cards.filter(c => commanderCardIds.includes(c.id)).map(c => c.name);
    const companionName = cards.find(c => c.id === companionCardId)?.name || null;

    const eventToPublish = prepareDeckEvent(
      null,
      title,
      format,
      cards,
      sideboardCards,
      commanderNames,
      companionName,
      bannerUrl,
      visibility
    );

    publishEvent(
      eventToPublish,
      {
        onSuccess: (e) => {
          toast({ title: "List published successfully!" });
          const naddr = nip19.naddrEncode({
            identifier: e.tags.find(t => t[0] === 'd')?.[1] || '',
            pubkey: e.pubkey,
            kind: e.kind,
          });
          const deck: Deck = {
            event: e,
            title: e.tags.find(t => t[0] === 'title')?.[1] || '',
            cards: cards.map(c => ({ name: c.name, quantity: c.count, foil: c.is_foil, set_id: c.set, art_id: c.collector_number })),
            sideboard: sideboardCards.map(c => ({ name: c.name, quantity: c.count, foil: c.is_foil, set_id: c.set, art_id: c.collector_number })),
            commanders: commanderNames,
            companion: companionName,
          };
          navigate(`/list/${naddr}`, { state: { deck } });
          clearList();
          clearSideboardList();
          setIsPublishConfirmOpen(false);
        },
        onError: () => {
          toast({ title: "Failed to publish list", variant: "destructive" });
        }
      }
    );
  };

  const handleShare = () => {
    const groupedCards = cards.reduce((acc, card) => {
      const key = `${card.id}-${card.is_foil}`;
      if (!acc[key]) {
        acc[key] = { ...card, quantity: 0 };
      }
      acc[key].quantity++;
      return acc;
    }, {} as Record<string, ScryfallCard & { quantity: number }>);

    const deck: Deck = {
      event: {
        kind: 37381,
        content: '',
        tags: [],
        pubkey: user?.pubkey || '',
        created_at: Math.floor(Date.now() / 1000),
        id: '',
        sig: '',
      },
      title,
      cards: Object.values(groupedCards).map(c => ({ name: c.name, quantity: c.quantity, foil: c.is_foil, set_id: c.set, art_id: c.collector_number })),
      sideboard: sideboardCards.map(c => ({ name: c.name, quantity: c.count, foil: c.is_foil, set_id: c.set, art_id: c.collector_number })),
      commanders: Object.values(groupedCards).filter(c => commanderCardIds.includes(c.id)).map(c => c.name),
      companion: Object.values(groupedCards).find(c => c.id === companionCardId)?.name,
    };
    setSelectedDeck(deck);
    setIsShareDialogOpen(true);
  };

  const handleBuyList = () => {
    const tcgPlayerMassEntryUrl = `${config.tcgPlayerAffiliateUrl}?subId1=deck-list-buy-all&u=https%3A%2F%2Fwww.tcgplayer.com%2Fmassentry%3Fproductline%3DMagic%26c%3D` +
      cards.map(c => {
        const cardName = (c.layout === 'transform' || c.layout === 'modal_dfc' || c.layout === 'flip') ? c.name.split(' // ')[0] : c.name;
        return encodeURIComponent(`1 ${cardName}`);
      }).join('%7C%7C');
    window.open(tcgPlayerMassEntryUrl, '_blank');
  };

  const edhrecButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={handleFetchRecommendations}
      disabled={!commanderCardIds.length}
      className={!commanderCardIds.length ? "hidden" : ""}
    >
      <Bot className="h-4 w-4 lg:mr-2" />
      <span className="text-xs lg:text-md">EDHREC</span>
    </Button>
  );

  useSeoMeta({
    title: 'Surveil - Build a Deck',
    description: 'Search for Magic: The Gathering cards and build your deck.',
  });

  return (
    <main className="flex-grow flex flex-col sm:mt-2">
      <div className="container mx-auto px-2 md:px-6 flex-grow">
          <div className="lg:grid lg:grid-cols-5 lg:gap-8 max-w-7xl mx-auto h-full">
              <div className="lg:col-span-3 flex flex-col">
                  {/* Combined Search and List for Mobile */}
                  <div className="lg:hidden flex flex-col h-full">
                      <MobileDeckBuilder
                        mainboardCardCount={cards.reduce((total, card) => total + card.count, 0)}
                        sideboardCardCount={sideboardCards.reduce((total, card) => total + card.count, 0)}
                        searchTabContent={
                          <div className="flex flex-col gap-4 h-full">
                            <CardSearch addCard={addCard} cards={cards} sideboardCards={sideboardCards} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                          </div>
                        }
                        edhrecButton={edhrecButton}
                        quickAdd={<QuickAddBar onCardAdded={addCard} />}
                        commanderCardIds={commanderCardIds}
                        listTabContent={
                          <>
                            <CardList
                              cards={cards}
                              sideboardCards={sideboardCards}
                              addCard={addCard}
                              removeCard={removeCard}
                              setCardCount={setCardCount}
                              updateCard={updateCard}
                              setBannerUrl={setBannerUrl}
                              commanderCardIds={commanderCardIds}
                              setCommanderCardIds={setCommanderCardIds}
                              companionCardId={companionCardId}
                              setCompanionCardId={setCompanionCardId}
                              openDeckBuilder={() => setIsDeckBuilderOpen(true)}
                              onMoveCard={(card) => {
                                const cardToMove = cards.find(c => c.id === card.id);
                                if (cardToMove) {
                                  removeCard(cardToMove);
                                  addSideboardCard(cardToMove);
                                }
                              }}
                              setSideboardCards={(cards) => cards.forEach(addSideboardCard)}
                            >
                            </CardList>
                            {sideboardCards.length > 0 && (
                              <div className="mt-4">
                                <h3 className="text-lg font-medium">Sideboard</h3>
                                <CardList
                                  cards={sideboardCards}
                                  addCard={addSideboardCard}
                                  removeCard={removeSideboardCard}
                                  setCardCount={setSideboardCardCount}
                                  updateCard={updateSideboardCard}
                                  setBannerUrl={() => {}}
                                  commanderCardIds={[]}
                                  setCommanderCardIds={() => {}}
                                  companionCardId={null}
                                  setCompanionCardId={() => {}}
                                  openDeckBuilder={() => {}}
                                  isSideboard
                                  onMoveCard={(card) => {
                                    const cardToMove = sideboardCards.find(c => c.id === card.id);
                                    if (cardToMove) {
                                      removeSideboardCard(cardToMove);
                                      addCard(cardToMove);
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </>
                        }
                        controls={
                        <ListControls
                          handleSave={handleOpenPublishDialog}
                          handleShare={handleShare}
                          handleClearList={() => {
                            clearList();
                            clearSideboardList();
                          }}
                          handleBuyList={handleBuyList}
                          isPending={isPending}
                        />
                        }
                      />
                  </div>

                  {/* Desktop Search */}
                  <div className="hidden lg:block space-y-8">
                      <CardSearch addCard={addCard} cards={cards} sideboardCards={sideboardCards} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                  </div>
              </div>

              {/* Sidebar for Desktop */}
              <div className="hidden lg:block lg:col-span-2 space-y-8 lg:sticky lg:top-24 h-[calc(100vh-5rem)]">
                  <div className="flex flex-col h-full">
                      <div className="flex-grow overflow-y-auto pr-2 min-h-0">
                          <CardList
                              cards={cards}
                              sideboardCards={sideboardCards}
                              addCard={addCard}
                              removeCard={removeCard}
                              setCardCount={setCardCount}
                              updateCard={updateCard}
                              setBannerUrl={setBannerUrl}
                              commanderCardIds={commanderCardIds}
                              setCommanderCardIds={setCommanderCardIds}
                              companionCardId={companionCardId}
                              setCompanionCardId={setCompanionCardId}
                              openDeckBuilder={() => setIsDeckBuilderOpen(true)}
                              onMoveCard={(card) => {
                                const cardToMove = cards.find(c => c.id === card.id);
                                if (cardToMove) {
                                  removeCard(cardToMove);
                                  addSideboardCard(cardToMove);
                                }
                              }}
                              setSideboardCards={(cards) => cards.forEach(addSideboardCard)}
                          >
                          {edhrecButton}
                          </CardList>
                          {sideboardCards.length > 0 && (
                            <div className="mt-4">
                              <h3 className="text-lg font-medium">Sideboard</h3>
                              <CardList
                                cards={sideboardCards}
                                addCard={addSideboardCard}
                                removeCard={removeSideboardCard}
                                setCardCount={setSideboardCardCount}
                                updateCard={updateSideboardCard}
                                setBannerUrl={() => {}}
                                commanderCardIds={[]}
                                setCommanderCardIds={() => {}}
                                companionCardId={null}
                                setCompanionCardId={() => {}}
                                openDeckBuilder={() => {}}
                                isSideboard
                                onMoveCard={(card) => {
                                  const cardToMove = sideboardCards.find(c => c.id === card.id);
                                  if (cardToMove) {
                                    removeSideboardCard(cardToMove);
                                    addCard(cardToMove);
                                  }
                                }}
                              />
                            </div>
                          )}
                      </div>
                      <div className="mt-4 flex-shrink-0">
                            <ListControls
                                handleSave={handleOpenPublishDialog}
                                handleShare={handleShare}
                                handleClearList={() => {
                                  clearList();
                                  clearSideboardList();
                                }}
                                handleBuyList={handleBuyList}
                                isPending={isPending}
                            />
                      </div>
                  </div>
              </div>
          </div>
      </div>
      <ShareListDialog
          open={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          deck={selectedDeck}
      />
      <DeckBuilderModal
          open={isDeckBuilderOpen}
          onOpenChange={setIsDeckBuilderOpen}
          addCard={addCard}
          onFinish={(query) => handleDeckBuilderFinish(query)}
      />
      <PublishConfirmationDialog
        open={isPublishConfirmOpen}
        onOpenChange={setIsPublishConfirmOpen}
        onConfirm={handleSave}
        title={title}
        setTitle={setTitle}
        format={format}
        setFormat={setFormat}
        visibility={visibility}
        setVisibility={setVisibility}
        isPending={isPending}
      />
      <EDHRecModal
        isOpen={isEdhrecModalOpen}
        onClose={() => setIsEdhrecModalOpen(false)}
        recommendations={edhrecData}
        isLoading={isEdhrecPending}
        isError={isEdhrecError}
        onCardAdded={addCard}
      />
  </main>
  );
};

export default Build;
